import {createFileRoute, useNavigate, useParams} from '@tanstack/react-router';
import {useQuery} from '@tanstack/react-query';
import {useEffect, useState} from 'react';
import {football} from '../football/api';
import {joinReady, startMobile, typedMobile} from '../football/newPlayer';
import {codeSentBy, offersTextInstead} from '../components/codeChannel';
import {useJwtAuth} from '../JWTAuthProvider';

/**
 * A football team's join link (football v2): the player gives their name (one is enough) and
 * mobile (starting at the club's calling code), says whether an unclaimed squad name is them —
 * nothing is answered for them — proves the mobile by OTP, and is on the squad and signed in.
 * No password, no email.
 */
export const Route = createFileRoute('/join-team/$token')({
    component: JoinTeam,
});

function JoinTeam() {
    const params = useParams({strict: false}) as {token: string};
    const jwt = useJwtAuth();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [mobile, setMobile] = useState('');
    // "Is this you on the squad list?": a squad row, 'new', or not answered yet (undefined).
    const [claim, setClaim] = useState<string | undefined>(undefined);
    const [code, setCode] = useState('');
    const [step, setStep] = useState<'name' | 'code' | 'done'>('name');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const team = useQuery({queryKey: ['team-join', params.token], queryFn: () => football.joinShow(params.token), enabled: !!params.token});
    const looks = useQuery({queryKey: ['team-join', params.token, name], queryFn: () => football.joinShow(params.token, name), enabled: !!params.token && name.trim().length >= 2});
    const lookalikes: {name: string; uid: string}[] = looks.data?.lookalikes || [];
    // An answer about a name no longer offered (the name typed changed) is no answer.
    const answer = claim === 'new' || lookalikes.some((l) => l.uid === claim) ? claim : undefined;
    const dialCode: string | undefined = team.data?.dial_code;
    // The mobile starts at the club's calling code (+971 for a UAE club), once the page knows it.
    useEffect(() => { if (dialCode) setMobile((m) => (m.trim() === '' ? startMobile(dialCode) : m)); }, [dialCode]);
    const ready = joinReady({name, mobile, dialCode, lookalikes, claim: answer});

    // How the code went: 'whatsapp' first, 'sms' when WhatsApp could not take it or they asked.
    const [channel, setChannel] = useState('sms');
    const send = async (textInstead = false) => {
        setBusy(true); setError(null);
        try {
            const res = await football.join({token: params.token, name: name.trim(), mobile: typedMobile(mobile, dialCode), ...(textInstead ? {channel: 'sms'} : {})});
            setChannel(res?.channel || 'sms');
            setStep('code');
        } catch (e: any) { setError(e?.message || 'Could not send the code. Check the number.'); }
        setBusy(false);
    };
    const check = async () => {
        setBusy(true); setError(null);
        try {
            const res = await football.join({token: params.token, name: name.trim(), mobile: typedMobile(mobile, dialCode), code, claim: answer === 'new' ? null : answer});
            if (res?.access_token) jwt.setFieldsFromResponse(res);
            setStep('done');
        } catch (e: any) { setError(e?.message || 'That code did not work. Try again.'); }
        setBusy(false);
    };

    const input = 'mt-1 w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-white placeholder:text-white/40';
    const button = 'mt-4 w-full rounded-full bg-[#f5c518] px-6 py-3 text-sm font-bold text-[#04102e] disabled:opacity-50';

    return (
        <div className="min-h-screen bg-[#04102e] px-4 py-10 text-white">
            <div className="mx-auto max-w-md">
                {team.isLoading && <p className="text-white/70">Loading…</p>}
                {team.isError && <p className="text-white/70">This link is not one we know.</p>}
                {team.data && (
                    <>
                        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#cddbff]">Join the squad</p>
                        <h1 className="mt-2 text-3xl font-bold">{team.data.team?.name}</h1>
                        {team.data.event?.title && <p className="text-white/70">{team.data.event.title}</p>}

                        {step === 'name' && (
                            <div className="mt-6">
                                <label className="text-sm text-white/70">Your name<input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" /></label>
                                {lookalikes.length > 0 && (
                                    <div className="mt-3 rounded-xl border border-white/20 p-3" data-testid="is-this-you">
                                        <p className="text-sm text-white/80">Is this you on the squad list?</p>
                                        {lookalikes.map((l) => (
                                            <button key={l.uid} type="button" aria-pressed={answer === l.uid} onClick={() => setClaim(l.uid)}
                                                className={'mt-2 mr-2 rounded-full border px-3 py-1 text-sm ' + (answer === l.uid ? 'border-[#f5c518] bg-[#f5c518] text-[#04102e]' : 'border-white/30')}>
                                                {l.name}
                                            </button>
                                        ))}
                                        <button type="button" aria-pressed={claim === 'new'} onClick={() => setClaim('new')} className={'mt-2 rounded-full border px-3 py-1 text-sm ' + (claim === 'new' ? 'border-[#f5c518] bg-[#f5c518] text-[#04102e]' : 'border-white/30')}>No, I'm new</button>
                                        {answer === undefined && <p className="mt-2 text-xs text-[#f5c518]">Tap one: your name on the list, or No, I'm new.</p>}
                                    </div>
                                )}
                                <label className="mt-4 block text-sm text-white/70">Mobile<input className={input} inputMode="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder={startMobile(dialCode)} /></label>
                                <p className="mt-1 text-xs text-white/40">We send you a code on WhatsApp or by text to prove it's you. Include your country code.</p>
                                {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
                                <button type="button" className={button} disabled={busy || !ready} onClick={() => send()}>{busy ? 'Sending…' : 'Send me the code'}</button>
                            </div>
                        )}
                        {step === 'code' && (
                            <div className="mt-6">
                                <p className="text-sm text-white/70">Enter the code we sent {codeSentBy(channel)} to <strong className="text-white">{mobile}</strong>.</p>
                                <input className={input + ' text-center text-2xl tracking-[0.4em]'} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
                                {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
                                <button type="button" className={button} disabled={busy || code.replace(/\D/g, '').length < 4} onClick={check}>{busy ? 'Checking…' : 'Join the squad'}</button>
                                {offersTextInstead(channel) && (
                                    <button type="button" className="mt-3 w-full text-sm font-semibold text-white/80 underline" disabled={busy} onClick={() => send(true)}>Text me instead</button>
                                )}
                                <button type="button" className="mt-3 w-full text-sm text-white/60" onClick={() => setStep('name')}>Back</button>
                            </div>
                        )}
                        {step === 'done' && (
                            <div className="mt-6">
                                <p className="text-lg">You're on the squad. Your profile collects every game you play from here.</p>
                                <button type="button" className={button} onClick={() => navigate({to: '/profiles/me/'})}>See my profile</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
